import { Injectable } from '@angular/core'
import { HttpClient, HttpResponse, HttpErrorResponse } from '@angular/common/http'
import { BehaviorSubject, Observable, Subject, throwError } from 'rxjs'
import { shareReplay, map, catchError, distinctUntilChanged, filter } from 'rxjs/operators'
import { TranslateService } from '@ngx-translate/core'

import * as api from '@/config/api-config.json'

import { ApplicationUpdateService } from '@/services/application-update.service'
import { TokenRefreshService } from '@/services/token-refresh.service'
import { UIStateService } from '@/services/ui-state.service'
import { StorageMigrationService } from '@/services/storage-migration.service'
import { SessionStateService, SessionState } from '@/services/session-state.service'
import { log, warn, error } from '@/util/logger'

import { Merchant } from '@/domain/merchant'
import { Seller } from '@/domain/seller'
import { User } from '@/domain/user'
import { Store } from '@/domain/store'

@Injectable({ providedIn: 'root' })
export class AuthenticationService {

  private _currentUserSubject: BehaviorSubject<User>
  public currentUser: Observable<User>

  // Separate observables for different aspects of user state
  private _tokenUpdateSubject = new Subject<{ token: string, refresh_token: string }>()
  public tokenUpdate$ = this._tokenUpdateSubject.asObservable()

  // Observable that only emits when user data (not tokens) actually changes
  public userDataChanges$: Observable<User>

  private _preloadedMerchant: Merchant
  private _preloadedMerchantSubject: BehaviorSubject<Merchant>
  public preloadedMerchant: Observable<Merchant>

  private _legacyUpgradeInFlight = false
  private _legacyUpgradeSatisfied = false

  constructor(
    private _http: HttpClient,
    private _translation: TranslateService,
    private _applicationUpdate: ApplicationUpdateService,
    private _tokenRefresh: TokenRefreshService,
    private _uiState: UIStateService,
    private _storageMigration: StorageMigrationService,
    private _sessionState: SessionStateService
  ) {
    this._currentUserSubject = new BehaviorSubject<User>(null)
    this.currentUser = this._currentUserSubject.pipe(shareReplay(1))

    // Create an observable that only emits when actual user data changes (not just tokens)
    this.userDataChanges$ = this.currentUser.pipe(
      distinctUntilChanged((prev, curr) => {
        if (!prev && !curr) return true
        if (!prev || !curr) return false
        // Consider it the same if only tokens changed
        return prev.id === curr.id &&
               prev.email === curr.email &&
               prev.role === curr.role &&
               Merchant.equal(prev.merchant, curr.merchant) &&
               JSON.stringify(prev.seller) === JSON.stringify(curr.seller)
      }),
      shareReplay(1)
    )

    this._preloadedMerchant = null
    this._preloadedMerchantSubject = new BehaviorSubject<Merchant>(null)
    this.preloadedMerchant = this._preloadedMerchantSubject.asObservable()

    // Safely parse localStorage with error handling for backward compatibility
    let user = null
    try {
      const userStr = localStorage.getItem('currentUser')
      log('[AuthenticationService] Checking localStorage for existing session', {
        hasUserString: !!userStr,
        userStringLength: userStr?.length || 0
      })
      
      if (userStr) {
        user = JSON.parse(userStr)
        
        // Validate user object structure
        if (!user || typeof user !== 'object') {
          warn('[AuthenticationService] Invalid user format in localStorage, clearing')
          localStorage.removeItem('currentUser')
          user = null
        } else if (user.token && typeof user.token !== 'string') {
          warn('[AuthenticationService] Invalid token format in localStorage, clearing')
          localStorage.removeItem('currentUser')
          user = null
        } else {
          log('[AuthenticationService] Found valid user in localStorage', {
            userId: user.id,
            email: user.email,
            hasToken: !!user.token,
            hasRefreshToken: !!user.refresh_token
          })
        }
      }
    } catch (e) {
      error('[AuthenticationService] Failed to parse currentUser from localStorage, clearing corrupted data', e)
      localStorage.removeItem('currentUser')
      user = null
    }

    if (user) {
      try {
        if (user.merchant) {
          user.merchant = Merchant.from(user.merchant)
        }
        this._nextPreloadedMerchant(null)
        this._nextLoggedInUser(user)
        if (user.merchant) {
          this._maybeRedirectToMerchantHostname(user.merchant)
        }
        this._legacyUpgradeSatisfied = !!user.refresh_token
        // Update session state
        log('[AuthenticationService] Restoring session from localStorage, transitioning to Authenticated')
        this._sessionState.transitionTo(SessionState.Authenticated, { source: 'localStorage' })
        // Set default store selections if not already present in UIState
        // (e.g., new tab where sessionStorage is empty)
        this._ensureDefaultStoreSelections(user)
      } catch (e) {
        error('[AuthenticationService] Failed to restore user from localStorage', e)
        // Clear corrupted data and continue as logged out
        localStorage.removeItem('currentUser')
        this._sessionState.transitionTo(SessionState.LoggedOut)
      }
    } else {
      // No user in localStorage
      log('[AuthenticationService] No user found in localStorage, transitioning to LoggedOut')
      this._sessionState.transitionTo(SessionState.LoggedOut)
    }

    // Listen for localStorage changes from other tabs
    window.addEventListener('storage', (event: StorageEvent) => {
      if (event.key === 'currentUser') {
        log('[AuthenticationService] Storage event received for currentUser', {
          hasNewValue: !!event.newValue,
          hasOldValue: !!event.oldValue,
          currentState: this._sessionState.currentState
        })
        
        if (event.newValue === null) {
          // Another tab logged out - sync this tab
          log('[AuthenticationService] Another tab logged out, syncing this tab')
          this._currentUserSubject.next(null)
          this._sessionState.transitionTo(SessionState.LoggedOut, { source: 'storage_event' })
        } else if (event.newValue) {
          // Another tab updated user (e.g., login, token refresh) - sync this tab
          try {
            const updatedUser = JSON.parse(event.newValue)
            if (updatedUser.merchant) {
              updatedUser.merchant = Merchant.from(updatedUser.merchant)
            }

            const currentUser = this._currentUserSubject.value
            if (currentUser && currentUser.id === updatedUser.id) {
              // Same user - check if tokens actually changed
              const tokenChanged = currentUser.token !== updatedUser.token
              const refreshTokenChanged = currentUser.refresh_token !== updatedUser.refresh_token

              if (tokenChanged || refreshTokenChanged) {
                // Tokens changed - update and notify subscribers so WebSocket reconnects
                currentUser.token = updatedUser.token
                currentUser.refresh_token = updatedUser.refresh_token
                this._currentUserSubject.next(currentUser)
                log('[AuthenticationService] Tokens updated from another tab', {
                  userId: currentUser.id,
                  tokenChanged: true,
                  refreshTokenChanged: true
                })
                // Notify subscribers (CableService, health monitor, etc.)
                this._tokenUpdateSubject.next({
                  token: currentUser.token,
                  refresh_token: currentUser.refresh_token
                })
                // Restore session state if it was expired or errored -
                // the other tab successfully refreshed, so this tab's session is valid.
                // Note: Don't transition if state is Refreshing - the orchestrator will
                // handle that transition after _applyRefreshResponse completes.
                const currentState = this._sessionState.currentState
                if (currentState === SessionState.Expired || currentState === SessionState.Error) {
                  this._sessionState.transitionTo(SessionState.Authenticated, { source: 'storage_event_token_update' })
                }
              }
            } else {
              // Different user or login - this is a login from another tab
              log('[AuthenticationService] User logged in from another tab, syncing this tab', {
                userId: updatedUser.id,
                email: updatedUser.email,
                previousState: this._sessionState.currentState
              })
              this._nextLoggedInUser(updatedUser)
              this._sessionState.transitionTo(SessionState.Authenticated, { source: 'storage_event' })
              // Set default store selections on login if no existing selections
              this._ensureDefaultStoreSelections(updatedUser)
            }
          } catch (e) {
            warn('[AuthenticationService] Failed to parse updated user from storage event', e)
          }
        }
      }
    })

    setTimeout(() => this._maybeUpgradeLegacySession('init'), 0)
    this._applicationUpdate.heartbeat().subscribe(() => this._maybeUpgradeLegacySession('heartbeat'))
  }

  // Doing this in the constructor caused a circular DI dependency, gofigure
  maybePreloadMerchant(): void {
    if (!this.isLoggedIn) {
      this._preloadMerchantByHostname(window.location.hostname)
    }
  }

  /** Use this because if this service uses MerchantsService, it creates a circular dependency */
  public updateMerchants(merchants: Merchant[]): void {
    if (this.merchantId) {
      const merchant = merchants.find(m => { return m.id === this.merchantId })
      const user = this._currentUserSubject.value
      if (!Merchant.equal(merchant, user.merchant)) {
        user.merchant = merchant
        this._nextLoggedInUser(user)
      }
    }
  }

  get currentUserValue(): User|null {
    return this._currentUserSubject.value
  }

  get merchantId(): number|null {
    if (!this.currentUserValue) {
      return null
    }
    return this.isSeller
      ? (this.currentUserValue.seller?.merchant_id || null)
      : (this.currentUserValue.merchant?.id || null)
  }

  get sellerId(): number|null {
    return this.isSeller && this.currentUserValue?.seller ? this.currentUserValue.seller.id : null
  }

  get isLoggedIn(): boolean {
    return this._currentUserSubject.value !== null
  }

  get isAdmin() {
    return this.currentUserValue && this.currentUserValue.role === 'admin'
  }

  get isSeller() {
    return this.currentUserValue && this.currentUserValue.role === 'seller'
  }

  get isSellerReservationEnabled() {
    return this.currentUserValue?.merchant
      && this.currentUserValue.merchant.seller_reservations_enabled == true
      && this.isSeller
      && this.currentUserValue?.seller?.seller_reservations_enabled == true
  }

  get isCreateProductFeaturesEnabled() {
    return (this.isSeller && this.currentUserValue?.seller?.create_product_features_enabled) ||
      (this.isMerchant && this.currentUserValue?.merchant?.create_product_features_enabled)
  }

  get isMerchant() {
    return this.currentUserValue && this.currentUserValue.role === 'merchant'
  }

  whoami() {
    return this._http.get(api.whoami)
  }

  confirm(token: string) {
    return this._http
      .get<User>(api.auth.confirm + '?token=' + token)
      .pipe<User>(catchError(this._handleError))
  }

  reconfirm(token: string) {
    return this._http
      .get<User>(api.auth.reconfirm + '?token=' + token)
      .pipe<User>(catchError(this._handleError))
  }

  sellerSignup(values) {
    let body = {
      "seller": {
        "name": values.name,
        "phone": values.phone,
        "user_email": values.username,
        "accept_marketing": values.accept_marketing,
        "user_password": values.password,
        "user_password_confirmation": values.password_confirmation,
        "user_merchant_id": values.merchant_id
      }
    }
    return this._http
      .post<Seller>(api.sellers.signup, body)
      .pipe<Seller>(catchError(this._handleError))
  }

  logout() {
    // Call server to revoke refresh token and clear session
    this._http.delete(api.auth.logout).subscribe({
      next: () => this._completeLogout(),
      error: (err) => {
        warn('[AuthenticationService] Server logout failed:', err)
        this._completeLogout()
      }
    })
  }

  private _completeLogout() {
    localStorage.removeItem('currentUser')
    // Clear centralized UI state
    this._uiState.clearAll()
    // Still clear legacy sessionStorage for backwards compatibility
    sessionStorage.removeItem('pos.storeId')
    sessionStorage.removeItem('calendar.product_id')
    sessionStorage.removeItem('calendar.store_id')
    sessionStorage.removeItem('calendar.variation_id')
    sessionStorage.removeItem('sales-periods.store_id')
    sessionStorage.removeItem('dashboard.storeId')
    this._currentUserSubject.next(null)
    // Update session state
    this._sessionState.transitionTo(SessionState.LoggedOut)
    this._applicationUpdate.update()
  }

  login(username: string, password: string) {
    let body = {
      "user": {
        "email": username,
        "password": password
      }
    }
    return this._http.post<HttpResponse<User>>(api.auth.login, body, { observe: 'response' })
      .pipe(map(response => {
        let token = response.headers.get('Authorization')
        if (!token) {
          throw new Error('Could not log in')
        }
        token = token.replace("Bearer ", "")

        if (response.body['role'] == 'seller' && !response.body['seller_account_found']) {
          throw new Error('Seller account not found')
        }

        let user = new User()
        user.id = response.body['id']
        user.username = response.body['email']
        user.created_at = response.body['created_at']
        user.updated_at = response.body['updated_at']
        user.role = response.body['role']
        user.token = token
        user.refresh_token = response.body['refresh_token']  // Store refresh token
        user.seller = response.body['seller']
        user.merchant = Merchant.from(response.body['merchant'])

        if (user.seller) {
          user.merchant = user.seller.merchant
        }

        if (user.seller && user.seller.language && ['en','fi'].includes(user.seller.language)) {
          this._translation.use(user.seller.language)
        }

        if (this._shouldRedirect(user.merchant)) {
          this._maybeRedirectToMerchantHostname(user.merchant)
          return null
        } else {
          this._nextPreloadedMerchant(null)
          this._nextLoggedInUser(user)
          // Update session state
          this._sessionState.transitionTo(SessionState.Authenticated, { source: 'login' })
          // Set default store selections on login if no existing selections
          this._ensureDefaultStoreSelections(user)
          return user
        }
      }))
  }

  requestPassword(email: string) {
    return this._http
      .post(api.auth.request_password, { 'email': email })
      .pipe(catchError(this._handleError))
  }

  resetPassword(token: string, password: string, passwordConfirmation: string) {
    let body = {
      'password': password,
      'password_confirmation': passwordConfirmation,
      'reset_password_token': token
    }
    return this._http
      .post(api.auth.reset_password, body)
      .pipe(catchError(this._handleError))
  }

  refreshToken(): Observable<User> {
    log('[AuthenticationService] refreshToken() called')
    // Read from localStorage to get the latest refresh token
    // (another tab may have updated it since this tab loaded)
    const storedUser = localStorage.getItem('currentUser')
    const currentUser = storedUser ? JSON.parse(storedUser) : this.currentUserValue

    if (!currentUser || !currentUser.refresh_token) {
      warn('[AuthenticationService] No refresh token available for refresh')
      return throwError('No user logged in')
    }

    log('[AuthenticationService] Initiating token refresh with TokenRefreshService', {
      userId: currentUser.id
    })
    return this._tokenRefresh.refreshToken(currentUser.refresh_token).pipe(
      map(response => {
        log('[AuthenticationService] Token refresh response received', { 
          hasToken: !!response.token,
          hasRefreshToken: !!response.refresh_token 
        })
        const updatedUser = this._applyRefreshResponse(response)
        return updatedUser || currentUser
      })
    )
  }

  updateUserToken(token: string): void {
    const currentUser = this.currentUserValue
    if (currentUser) {
      currentUser.token = token
      this._nextLoggedInUser(currentUser)
    }
  }

  private _preloadMerchantByHostname(hostname: string) {
    this._http.get<Merchant>(`${api.merchants.find}?hostname=${hostname}`).subscribe({
      next: merchantResponse => {
        if (!merchantResponse) {
          log('[AuthenticationService] No merchant found for hostname', hostname)
          return
        }
        let merchant = Merchant.from(merchantResponse)
        this._nextPreloadedMerchant(merchant)
      },
      error: () => {
        // Silently ignore - preloading is optional and endpoint may require auth
      }
    })
  }

  private _nextPreloadedMerchant(merchant: Merchant) {
    this._preloadedMerchant = merchant
    this._preloadedMerchantSubject.next(this._preloadedMerchant)
    this._maybeRedirectToMerchantHostname(merchant)
  }

  private _shouldRedirect(merchant?: Merchant): boolean {
    return merchant && merchant.hostname && window.location.hostname != merchant.hostname
  }

  private _maybeRedirectToMerchantHostname(merchant?: Merchant): void {
    if (this._shouldRedirect(merchant)) {
      const goto = 'https://' + merchant.hostname
      if (this.isLoggedIn) {
        this.logout()
      }
      window.location.replace(goto)
    }
  }

  private _nextLoggedInUser(user: User) {
    localStorage.setItem('currentUser', JSON.stringify(user))
    this._currentUserSubject.next(user)
    this._legacyUpgradeSatisfied = !!user?.refresh_token
    
    log('[AuthenticationService] User state updated', {
      userId: user?.id,
      email: user?.email,
      role: user?.role,
      hasToken: !!user?.token,
      hasRefreshToken: !!user?.refresh_token,
      hasMerchant: !!user?.merchant,
      merchantId: user?.merchant?.id,
      storeCount: user?.merchant?.stores?.length || 0
    })
  }

  /**
   * Set default store selections on login if no existing selections are present.
   * This only applies at login time, not when user manually deselects stores.
   */
  private _ensureDefaultStoreSelections(user: User): void {
    if (!user || !user.merchant || !user.merchant.stores) {
      return
    }

    // Find the default store
    const defaultStore = user.merchant.stores.find((store: Store) => store.default === true)
    if (!defaultStore) {
      log('[AuthenticationService] No default store found, skipping default store selection')
      return
    }

    log('[AuthenticationService] Default store found, setting selections if not already set', {
      defaultStoreId: defaultStore.id,
      defaultStoreName: defaultStore.name
    })

    // Helper to check if a store ID is valid for this merchant
    const isValidStoreId = (storeId: number | string | null): boolean => {
      if (!storeId) return false
      const id = typeof storeId === 'string' ? parseInt(storeId, 10) : storeId
      return !isNaN(id) && user.merchant.stores.some((s: Store) => s.id === id)
    }

    // Dashboard: check sessionStorage directly
    // Only write legacy key if user hasn't migrated yet
    if (this._storageMigration.shouldUseLegacyKeys()) {
      const dashboardStoreId = sessionStorage.getItem('dashboard.storeId')
      if (!isValidStoreId(dashboardStoreId)) {
        sessionStorage.setItem('dashboard.storeId', defaultStore.id.toString())
        log('[AuthenticationService] Set default store for dashboard', { storeId: defaultStore.id })
      }
    }

    // POS: check sessionStorage directly (UIState loads from sessionStorage)
    const uiStateStored = sessionStorage.getItem('deux_ui')
    let posStoreId = null
    let calendarStoreId = null
    if (uiStateStored) {
      try {
        const parsed = JSON.parse(uiStateStored)
        posStoreId = parsed.posSelectedStore?.id || null
        calendarStoreId = parsed.calendarSelectedStore?.id || null
      } catch (e) {
        // If parsing fails, treat as no selection
      }
    }

    // POS: set default if no existing selection or if selection is invalid
    if (!isValidStoreId(posStoreId)) {
      this._uiState.setPosStore(defaultStore)
      log('[AuthenticationService] Set default store for POS', { storeId: defaultStore.id })
    }

    // Calendar: set default if no existing selection or if selection is invalid
    // Also check legacy calendar.store_id for backward compatibility (only if not migrated)
    let legacyCalendarStoreId = null
    if (this._storageMigration.shouldUseLegacyKeys()) {
      legacyCalendarStoreId = sessionStorage.getItem('calendar.store_id')
    }
    const hasValidCalendarSelection = isValidStoreId(calendarStoreId) || isValidStoreId(legacyCalendarStoreId)
    
    if (!hasValidCalendarSelection) {
      this._uiState.setCalendarStore(defaultStore)
      // Only set legacy sessionStorage key if user hasn't migrated yet
      if (this._storageMigration.shouldUseLegacyKeys()) {
        sessionStorage.setItem('calendar.store_id', defaultStore.id.toString())
      }
      log('[AuthenticationService] Set default store for calendar', { storeId: defaultStore.id })
    }

    // Sales period list: check sessionStorage directly
    const salesPeriodsStoreId = sessionStorage.getItem('sales-periods.store_id')
    if (!isValidStoreId(salesPeriodsStoreId)) {
      sessionStorage.setItem('sales-periods.store_id', defaultStore.id.toString())
      log('[AuthenticationService] Set default store for sales period list', { storeId: defaultStore.id })
    }
  }

  private _applyRefreshResponse(response: any): User|null {
    if (!response) {
      return this.currentUserValue
    }

    const currentUser = this.currentUserValue
    if (!currentUser) {
      return null
    }

    const tokenChanged = response.token && response.token !== currentUser.token
    const refreshTokenChanged = response.refresh_token && response.refresh_token !== currentUser.refresh_token

    // Check if actual user data changed (not just tokens)
    let dataChanged = false

    if (response.user) {
      const refreshedUser = response.user

      // Check for actual data changes
      dataChanged = currentUser.id !== refreshedUser.id ||
                    currentUser.email !== refreshedUser.email ||
                    currentUser.role !== refreshedUser.role ||
                    currentUser.updated_at !== refreshedUser.updated_at

      currentUser.id = refreshedUser.id
      currentUser.email = refreshedUser.email
      currentUser.role = refreshedUser.role
      currentUser.updated_at = refreshedUser.updated_at

      if (refreshedUser.merchant) {
        const newMerchant = Merchant.from(refreshedUser.merchant)
        if (!Merchant.equal(currentUser.merchant, newMerchant)) {
          dataChanged = true
        }
        currentUser.merchant = newMerchant

        // Update UI state service with new stores if they changed
        if (newMerchant && newMerchant.stores) {
          this._uiState.updateStoresPreservingSelections(newMerchant.stores)
        }
      }

      if (refreshedUser.seller) {
        if (JSON.stringify(currentUser.seller) !== JSON.stringify(refreshedUser.seller)) {
          dataChanged = true
        }
        currentUser.seller = refreshedUser.seller
      }
    }

    if (response.token) {
      currentUser.token = response.token
    }

    if (response.refresh_token) {
      currentUser.refresh_token = response.refresh_token
    }

    log('[AuthenticationService] Applying refresh response', {
      tokenChanged,
      refreshTokenChanged,
      dataChanged,
      userId: currentUser.id,
      hasMerchant: !!currentUser.merchant,
      merchantId: currentUser.merchant?.id,
      storeCount: currentUser.merchant?.stores?.length || 0
    })

    // Emit token update event if tokens changed
    if (tokenChanged || refreshTokenChanged) {
      log('[AuthenticationService] Emitting token update event', {
        tokenChanged,
        refreshTokenChanged,
        userId: currentUser.id
      })
      this._tokenUpdateSubject.next({
        token: currentUser.token,
        refresh_token: currentUser.refresh_token
      })
    }

    // Always update localStorage and emit to currentUser subject
    // Components should use userDataChanges$ if they only care about data changes
    this._nextLoggedInUser(currentUser)
    return currentUser
  }

  private _maybeUpgradeLegacySession(source: string): void {
    if (this._legacyUpgradeSatisfied || this._legacyUpgradeInFlight) {
      return
    }

    const currentUser = this.currentUserValue
    if (!currentUser) {
      return
    }

    if (currentUser.refresh_token) {
      this._legacyUpgradeSatisfied = true
      return
    }

    this._legacyUpgradeInFlight = true
    this._tokenRefresh.refreshToken(undefined).subscribe({
      next: (response) => {
        this._legacyUpgradeInFlight = false
        this._legacyUpgradeSatisfied = true  // Mark as satisfied regardless of outcome to prevent retry loop
        if (response) {
          this._applyRefreshResponse(response)
        }
      },
      error: (error) => {
        this._legacyUpgradeInFlight = false
        this._legacyUpgradeSatisfied = true  // Don't retry on error either
        warn(`[AuthenticationService] Legacy session upgrade failed from ${source}`, error)
      }
    })
  }

  private _handleError(error: HttpErrorResponse) {
    return throwError(error)
  }
}